import { useState, useEffect } from 'react';
import { PageLayout } from '../components/layout/PageLayout';
import { CampaignCard } from '../components/campaign/CampaignCard';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Search, Heart, Loader2, History, ChevronDown, ChevronUp } from 'lucide-react';

const PAST_INITIAL_COUNT = 6;

export default function DiscoverPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [pastCampaigns, setPastCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pastLoading, setPastLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [showAllPast, setShowAllPast] = useState(false);

  const fetchCampaigns = async (search = '') => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      const [activeRes, pastRes] = await Promise.all([
        api.get(`/campaigns/discover?${params}`),
        api.get(`/campaigns/past?${params}`),
      ]);
      setCampaigns(activeRes.data || []);
      setPastCampaigns(pastRes.data || []);
    } catch (error) {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
      setPastLoading(false);
      setSearching(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearching(true);
    setShowAllPast(false);
    fetchCampaigns(searchQuery);
  };

  const visiblePast = showAllPast ? pastCampaigns : pastCampaigns.slice(0, PAST_INITIAL_COUNT);
  const hasMorePast = pastCampaigns.length > PAST_INITIAL_COUNT;

  return (
    <PageLayout>
      {/* Hero Section */}
      <div className="relative bg-gradient-to-b from-primary/10 to-transparent py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Heart className="w-4 h-4" fill="currentColor" />
              Transparent crowdfunding
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-foreground mb-6 leading-[1.05] tracking-tight uppercase">
              <span className="block">Collect funds.</span>
              <span className="block">Upload receipts.</span>
              <span className="block text-primary">Repeat.</span>
            </h1>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Discover verified fundraising campaigns. Your contribution
              helps people and causes that matter to you.
            </p>

            {/* Search Bar */}
            <form
              onSubmit={handleSearch}
              className="flex gap-3 max-w-xl mx-auto"
            >
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search campaigns..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-14 rounded-full text-base bg-white shadow-lg border-0"
                  data-testid="search-input"
                />
              </div>
              <Button
                type="submit"
                className="h-14 px-8 rounded-full font-semibold shadow-lg"
                disabled={searching}
                data-testid="search-btn"
              >
                {searching ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Search'
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Active Campaigns */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-4">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-muted-foreground">Loading campaigns...</p>
            </div>
          </div>
        ) : campaigns.length === 0 ? (
          <div className="text-center py-20" data-testid="no-campaigns-message">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-secondary flex items-center justify-center">
              <Heart className="w-10 h-10 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-2">
              No active campaigns found
            </h3>
            <p className="text-muted-foreground">
              {searchQuery
                ? 'Try a different search term'
                : 'Be the first to start a campaign!'}
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-bold text-foreground">
                Active Campaigns
              </h2>
              <p className="text-muted-foreground">
                {campaigns.length} campaign{campaigns.length !== 1 ? 's' : ''}
              </p>
            </div>
            <div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-children"
              data-testid="campaigns-grid"
            >
              {campaigns.map((campaign) => (
                <CampaignCard key={campaign.id} campaign={campaign} />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Past Campaigns Section */}
      {!loading && (pastLoading || pastCampaigns.length > 0) && (
        <div className="border-t border-border/40 bg-secondary/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="past-campaigns-section">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
              <div>
                <div className="inline-flex items-center gap-2 bg-muted text-muted-foreground px-3 py-1 rounded-full text-xs font-medium mb-3">
                  <History className="w-3.5 h-3.5" />
                  Archive
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
                  Past Campaigns
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Campaigns that have ended — see the impact they made.
                </p>
              </div>
              {!pastLoading && (
                <p className="text-sm text-muted-foreground">
                  {pastCampaigns.length} campaign{pastCampaigns.length !== 1 ? 's' : ''}
                </p>
              )}
            </div>

            {pastLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-4 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
              </div>
            ) : (
              <>
                <div
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-children opacity-95"
                  data-testid="past-campaigns-grid"
                >
                  {visiblePast.map((campaign) => (
                    <CampaignCard key={campaign.id} campaign={campaign} showStatus />
                  ))}
                </div>
                {hasMorePast && (
                  <div className="flex justify-center mt-8">
                    <Button
                      variant="outline"
                      className="rounded-full gap-2"
                      onClick={() => setShowAllPast((v) => !v)}
                      data-testid="past-campaigns-toggle-btn"
                    >
                      {showAllPast ? (
                        <>
                          <ChevronUp className="w-4 h-4" />
                          Show less
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-4 h-4" />
                          Show all {pastCampaigns.length} past campaigns
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </PageLayout>
  );
}
